import { NavLink, Redirect, useHistory, useRouteMatch } from 'react-router-dom'
import { Button, Menu } from 'semantic-ui-react'
import { AxiosError } from 'axios'
import { Helmet } from 'react-helmet'
import * as React from 'react'
import useSWR from 'swr'
import { z } from 'zod'

import { noteTemplateSchema } from '../../types'
import { validateResponse } from '../../utils'
import NoteTemplateEditor from '../../components/settings/NoteTemplateEditor'
import { useToasts } from '../../components/toasts/ToastsProvider'
import PageLoader from '../../components/pageLoader'
import { useApi } from '../../store/mainContext'

type RouteParams = { templateId?: string }

export default function NoteTemplatesSettingsPage() {
  const { params } = useRouteMatch<RouteParams>()
  const { templateId } = params

  const { addToast } = useToasts()
  const history = useHistory()
  const api = useApi()

  const [isCreating, setIsCreating] = React.useState(false)

  const templates = useSWR('note-templates', () =>
    api
      .get('note-templates')
      .then(validateResponse(z.array(noteTemplateSchema))),
  )

  const selectedTemplate = templates.data?.find((t) => t._id === templateId)

  // If in root "/settings/note-templates" URL, automatically navigate to first
  // template (if one exists)
  const maybeFirstTemplateId = templates.data?.[0]?._id
  if (!params.templateId && maybeFirstTemplateId) {
    return <Redirect to={`/settings/note-templates/${maybeFirstTemplateId}`} />
  }

  const handleAddTemplate = () => {
    setIsCreating(true)
    return api
      .post('note-templates', {
        name: 'New Template',
        description: '',
        body: '',
        status: 'Draft',
      })
      .then(validateResponse(noteTemplateSchema))
      .then(({ _id }) =>
        templates
          .mutate()
          .then(() => history.push('/settings/note-templates/' + _id)),
      )
      .catch((err: AxiosError) =>
        addToast(err.response?.data.message || err.message, {
          variant: 'danger',
        }),
      )
      .finally(() => setIsCreating(false))
  }

  if (templates.isLoading) return <PageLoader />

  if (templateId && !(templates.data || []).some((t) => t._id === templateId)) {
    return <Redirect to="/settings/note-templates" />
  }

  return (
    <div className="pt-16">
      <Helmet>
        <title>Settings &gt; Note Templates</title>
      </Helmet>

      <div className="flex items-start">
        <Menu vertical className="no-overrides min-w-72 shrink-0">
          <Menu.Item header className="!flex items-center !pr-3">
            <div className="grow font-semibold">Templates</div>
            <Button
              onClick={handleAddTemplate}
              disabled={isCreating}
              loading={isCreating}
              icon="add"
              className="!mr-0"
              compact
              basic
            />
          </Menu.Item>
          {templates.data?.map((template) => {
            return (
              <Menu.Item
                to={`/settings/note-templates/${template._id}`}
                key={template._id}
                as={NavLink}
              >
                <span className="text-slate-700">{template.name}</span>
                {template.description && (
                  <div className="mt-1 line-clamp-2 text-sm leading-tight text-slate-500">
                    {template.description}
                  </div>
                )}
              </Menu.Item>
            )
          })}
        </Menu>

        <div className="grow px-9">
          {selectedTemplate ? (
            <NoteTemplateEditor
              onSuccess={() => templates.mutate()}
              template={selectedTemplate}
              key={selectedTemplate?._id}
            />
          ) : (
            <div className="py-4 text-center italic text-slate-500">
              Select a template
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
